import React, { useState, useEffect } from 'react';
import AffirmationCard from '../components/AffirmationCard';
import { Heart, Calendar, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import apiService from '../services/api';

const SelfLoveWall = () => {
  const [savedAffirmations, setSavedAffirmations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    loadSavedAffirmations();
  }, []);

  const loadSavedAffirmations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const affirmations = await apiService.getSavedAffirmations();
      setSavedAffirmations(affirmations);
    } catch (error) {
      setError(error.message);
      console.error('Failed to load saved affirmations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsave = async (affirmationId) => {
    try {
      const response = await apiService.saveAffirmation(affirmationId);
      
      // Remove from local state
      setSavedAffirmations(prev => prev.filter(item => item.id !== affirmationId));
      
      toast({
        title: "Success!",
        description: response.message,
        duration: 3000,
      });
      
    } catch (error) {
      toast({
        title: "Unsave Failed",
        description: error.message,
        duration: 3000,
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Convert to local timezone and format as "MM/DD/YYYY h:mm AM/PM"
    return date.toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMoodEmoji = (mood) => {
    const moodEmojis = {
      1: '😔',
      2: '😕',
      3: '😐', 
      4: '🙂',
      5: '😊'
    };
    return moodEmojis[mood] || '😊';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-rose-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading your Self-Love Wall...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-rose-500 mr-3" />
            <h1 className="text-3xl font-serif font-bold text-gray-800">Your Love Vault</h1>
            <Sparkles className="w-8 h-8 text-rose-500 ml-3" />
          </div>
          <p className="text-gray-600">
            A collection of your saved affirmations and moments of self-love
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-red-700 font-medium">Failed to Load</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
            <Button
              onClick={loadSavedAffirmations}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        )}

        {!error && savedAffirmations.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-rose-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Your wall is waiting for love
            </h3>
            <p className="text-gray-500 mb-6">
              Save affirmations from your daily check-ins to build your personal collection
            </p>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2 rounded-full"
            >
              Generate Your First Affirmation
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-rose-500" />
                <span className="text-gray-600 font-medium">
                  {savedAffirmations.length} saved affirmation{savedAffirmations.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <Button
                onClick={loadSavedAffirmations}
                variant="outline"
                size="sm"
                className="border-rose-300 text-rose-600 hover:bg-rose-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            <div className="grid gap-6">
              {savedAffirmations.map((item) => (
                <div key={item.id} className="relative">
                  <div className="absolute top-4 right-4 flex items-center space-x-3 text-xs text-rose-400 font-medium bg-white/70 px-3 py-1 rounded-full">
                    <span>{getMoodEmoji(item.mood)}</span>
                    <span>{formatDate(item.timestamp)}</span>
                  </div>
                  <AffirmationCard
                    affirmation={{
                      ...item,
                      saved: true
                    }}
                    onSave={() => handleUnsave(item.id)}
                    onShare={() => {/* Share functionality */}}
                    isSaved={true}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfLoveWall;